import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Grid,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    NavigateBefore,
    NavigateNext,
    Today
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/es';
import axios from 'axios';
import { UrlApi } from '../../services/apirest';
import Navbar from '../Navbar';

moment.locale('es');

const CanjeResumen = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [fechaSeleccionada, setFechaSeleccionada] = useState(moment());
    const [resumen, setResumen] = useState(null);
    const [tiposEnvase, setTiposEnvase] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        cargarTiposEnvase();
    }, []);

    useEffect(() => {
        if (tiposEnvase.length > 0) {
            cargarResumen();
        }
    }, [fechaSeleccionada, tiposEnvase]);

    const cargarTiposEnvase = async () => {
        try {
            const response = await axios.get(UrlApi + '/canjes_routes/canje_tipos_envase');
            setTiposEnvase(response.data);
        } catch (err) {
            console.error('Error al cargar tipos de envase:', err);
            setError('Error al cargar tipos de envase');
        }
    };

    const cargarResumen = async () => {
        try {
            setLoading(true);
            const fechaFormateada = fechaSeleccionada.format('YYYY-MM-DD');
            const response = await axios.get(`${UrlApi}/canjes_routes/canje_stock/resumen/${fechaFormateada}`);
            setResumen(response.data);
            setError(null);
        } catch (err) {
            console.error('Error al cargar resumen:', err);
            setError('Error al cargar el resumen');
        } finally {
            setLoading(false);
        }
    };

    const handleDiaAnterior = () => {
        setFechaSeleccionada(moment(fechaSeleccionada).subtract(1, 'days'));
    };

    const handleDiaSiguiente = () => {
        setFechaSeleccionada(moment(fechaSeleccionada).add(1, 'days'));
    };

    const handleHoy = () => {
        setFechaSeleccionada(moment());
    };

    const calcularTotalesGenerales = () => {
        if (!resumen || !resumen.sucursales) return {};

        const totales = {};
        tiposEnvase.forEach(tipo => {
            totales[tipo.capacidad_kg] = 0;
        });

        resumen.sucursales.forEach(sucursal => {
            Object.entries(sucursal.totales).forEach(([capacidad, cantidad]) => {
                totales[capacidad] = (totales[capacidad] || 0) + cantidad;
            });
        });

        return totales;
    };

    // Filtrar sucursales que tienen al menos un dato cargado
    const sucursalesConDatos = resumen?.sucursales?.filter(sucursal => {
        const tieneAlgunDato = Object.values(sucursal.totales).some(cantidad => cantidad > 0);
        return tieneAlgunDato;
    }) || [];

    const totalesGenerales = calcularTotalesGenerales();

    return (
        <>
            <Navbar />
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                    {/* Header FIJO con selector de fecha */}
                    <Paper
                        sx={{
                            p: isMobile ? 1.5 : 2,
                            position: isMobile ? 'fixed' : 'sticky',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1100,
                            boxShadow: 3
                        }}
                    >
                        <Box
                            display="flex"
                            flexDirection={isMobile ? 'column' : 'row'}
                            justifyContent="space-between"
                            alignItems={isMobile ? 'stretch' : 'center'}
                            pt={isMobile ? '60px' : 3}
                            gap={isMobile ? 1.5 : 0}

                        >
                            <Typography
                                variant={isMobile ? 'h6' : 'h5'}
                                component="h2"
                                sx={{ fontSize: isMobile ? '1rem' : '1.5rem' }}
                            >
                                Canje-Resumen Envases x Sucursal
                            </Typography>

                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent={isMobile ? 'center' : 'flex-end'}
                                gap={0.5}
                            >
                                <IconButton onClick={handleDiaAnterior} color="primary" size={isMobile ? 'small' : 'medium'}>
                                    <NavigateBefore />
                                </IconButton>

                                <DatePicker
                                    label="Fecha"
                                    value={fechaSeleccionada}
                                    onChange={(newValue) => setFechaSeleccionada(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            sx: { width: isMobile ? '120px' : '150px' }
                                        }
                                    }}
                                />

                                <IconButton onClick={handleDiaSiguiente} color="primary" size={isMobile ? 'small' : 'medium'}>
                                    <NavigateNext />
                                </IconButton>

                                <Button
                                    variant="outlined"
                                    startIcon={!isMobile && <Today />}
                                    onClick={handleHoy}
                                    size="small"
                                    sx={{ minWidth: isMobile ? '50px' : 'auto' }}
                                >
                                    HOY
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Contenido con SCROLL */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: isMobile ? 1 : 3, pt: isMobile ? '120px' : 3 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {loading ? (
                            <Box display="flex" justifyContent="center" my={4}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {/* Totales Generales */}
                                {sucursalesConDatos.length > 0 && (
                                    <Paper sx={{ p: isMobile ? 1.5 : 3, mb: isMobile ? 2 : 3, backgroundColor: '#e3f2fd' }}>
                                        <Typography
                                            variant={isMobile ? 'subtitle1' : 'h6'}
                                            gutterBottom
                                            sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.95rem' : '1.25rem' }}
                                        >
                                            Total Sucursales al {fechaSeleccionada.format('DD/MM/YYYY')}
                                        </Typography>
                                        <TableContainer sx={{ overflowX: 'auto' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                backgroundColor: '#90caf9',
                                                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                                padding: isMobile ? '4px' : '6px 8px',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            Estado
                                                        </TableCell>
                                                        {tiposEnvase.map(tipo => (
                                                            <TableCell
                                                                key={tipo.id}
                                                                align="right"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    backgroundColor: '#90caf9',
                                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                                    padding: isMobile ? '4px' : '6px 8px',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {tipo.capacidad_kg}Kgs
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                                padding: isMobile ? '4px' : '6px 8px'
                                                            }}
                                                        >
                                                            TOTALES
                                                        </TableCell>
                                                        {tiposEnvase.map(tipo => (
                                                            <TableCell
                                                                key={tipo.id}
                                                                align="right"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    fontSize: isMobile ? '0.85rem' : '1.1rem',
                                                                    padding: isMobile ? '4px' : '6px 8px'
                                                                }}
                                                            >
                                                                {totalesGenerales[tipo.capacidad_kg] || 0}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                )}

                                {/* Cards por Sucursal */}
                                {sucursalesConDatos.length > 0 ? (
                                    <Grid container spacing={isMobile ? 2 : 3}>
                                        {sucursalesConDatos.map(sucursal => {
                                            const empresasConDatos = Object.values(sucursal.empresas).filter(empresa => {
                                                return Object.values(empresa.envases).some(cantidad => cantidad > 0);
                                            });

                                            return (
                                                <Grid item xs={12} md={6} key={sucursal.sucursal_id}>
                                                    <Card sx={{ boxShadow: 3 }}>
                                                        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                                                            <Box
                                                                display="flex"
                                                                flexDirection={isMobile ? 'column' : 'row'}
                                                                justifyContent="space-between"
                                                                alignItems={isMobile ? 'flex-start' : 'center'}
                                                                mb={isMobile ? 1 : 2}
                                                                gap={isMobile ? 0.5 : 0}
                                                            >
                                                                <Typography
                                                                    variant={isMobile ? 'subtitle1' : 'h6'}
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: '#1976d2',
                                                                        fontSize: isMobile ? '0.95rem' : '1.25rem'
                                                                    }}
                                                                >
                                                                    {sucursal.sucursal_id} {sucursal.sucursal}
                                                                </Typography>
                                                                {sucursal.ultima_actualizacion && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: '#666',
                                                                            fontSize: isMobile ? '0.7rem' : '0.9rem'
                                                                        }}
                                                                    >
                                                                        @{moment(sucursal.ultima_actualizacion).format('hh:mm:ss a')}
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            <TableContainer sx={{ overflowX: 'auto' }}>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell
                                                                                sx={{
                                                                                    fontWeight: 'bold',
                                                                                    backgroundColor: '#f5f5f5',
                                                                                    fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                                    padding: isMobile ? '4px' : '6px 8px',
                                                                                    whiteSpace: 'nowrap'
                                                                                }}
                                                                            >
                                                                                Estado
                                                                            </TableCell>
                                                                            {tiposEnvase.map(tipo => (
                                                                                <TableCell
                                                                                    key={tipo.id}
                                                                                    align="right"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        backgroundColor: '#f5f5f5',
                                                                                        fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                                        padding: isMobile ? '4px' : '6px 8px',
                                                                                        whiteSpace: 'nowrap'
                                                                                    }}
                                                                                >
                                                                                    {tipo.capacidad_kg}Kgs
                                                                                </TableCell>
                                                                            ))}
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {empresasConDatos.map(empresa => (
                                                                            <TableRow key={empresa.empresa_id} hover>
                                                                                <TableCell
                                                                                    sx={{
                                                                                        fontWeight: 500,
                                                                                        fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                                        padding: isMobile ? '4px' : '6px 8px',
                                                                                        whiteSpace: 'nowrap'
                                                                                    }}
                                                                                >
                                                                                    {empresa.nombre_empresa}
                                                                                </TableCell>
                                                                                {tiposEnvase.map(tipo => (
                                                                                    <TableCell
                                                                                        key={tipo.id}
                                                                                        align="right"
                                                                                        sx={{
                                                                                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                                            padding: isMobile ? '4px' : '6px 8px'
                                                                                        }}
                                                                                    >
                                                                                        {empresa.envases[tipo.capacidad_kg] || 0}
                                                                                    </TableCell>
                                                                                ))}
                                                                            </TableRow>
                                                                        ))}

                                                                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                                                            <TableCell
                                                                                sx={{
                                                                                    fontWeight: 'bold',
                                                                                    fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                                    padding: isMobile ? '4px' : '6px 8px',
                                                                                    whiteSpace: 'nowrap'
                                                                                }}
                                                                            >
                                                                                {sucursal.sucursal_id}-Total
                                                                            </TableCell>
                                                                            {tiposEnvase.map(tipo => (
                                                                                <TableCell
                                                                                    key={tipo.id}
                                                                                    align="right"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                                                        padding: isMobile ? '4px' : '6px 8px'
                                                                                    }}
                                                                                >
                                                                                    {sucursal.totales[tipo.capacidad_kg] || 0}
                                                                                </TableCell>
                                                                            ))}
                                                                        </TableRow>
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                ) : (
                                    <Alert severity="info">
                                        No hay datos registrados para esta fecha
                                    </Alert>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </LocalizationProvider>
        </>
    );
};

export default CanjeResumen;